package com.eladfinish.bible.contest.screens.pairMatching.ui

import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.extensions.makeBoldByAsterisksAnnotated
import com.eladfinish.bible.contest.screens.pairMatching.model.KeyItem
import com.eladfinish.bible.contest.ui.common.PreviewTheme

// ── Color palette matching the original Android View implementation ────────────
private val colorAmber = Color(0xFFFFC107)  // AMBER — idle drop target
private val colorGreen = Color(0xFF1B5E20)  // dark green — matched
private val colorOrange = Color(0xFFFF6800) // ORANGE — hover highlight

@Composable
fun PairMatchingKeyItemView(
    item: KeyItem,
    isDragHovering: Boolean,
    modifier: Modifier = Modifier,
) {
    val backgroundColor = when {
        item.isMatched -> colorGreen
        isDragHovering -> colorOrange
        else -> colorAmber
    }
    val textColor = if (item.isMatched) Color.White else Color.Black

    Card(
        modifier = modifier
            .fillMaxWidth()
            .heightIn(min = 48.dp)
            .padding(horizontal = 4.dp, vertical = 6.dp),
        shape = RoundedCornerShape(8.dp),
        colors = CardDefaults.cardColors(containerColor = backgroundColor),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
    ) {
        val displayText = buildKeyDisplayText(item)
        Text(
            text = displayText,
            modifier = Modifier
                .fillMaxWidth()
                .padding(8.dp),
            textAlign = TextAlign.Center,
            fontSize = 18.sp,
            color = textColor,
        )
    }
}

/** Builds the display text: "keyText — matchedValue" (with bold applied to asterisks). */
private fun buildKeyDisplayText(item: KeyItem): androidx.compose.ui.text.AnnotatedString {
    val rawKey = item.rawText
    val rawValue = item.matchedValueRaw
    val combined = if (rawValue != null) "$rawKey — $rawValue" else rawKey
    return combined.makeBoldByAsterisksAnnotated()
}

// ─── Previews ─────────────────────────────────────────────────────────────────

@Preview(showBackground = true, name = "KeyItem — idle")
@Composable
private fun KeyItemIdlePreview() {
    PreviewTheme {
        PairMatchingKeyItemView(
            item = KeyItem(id = 0, rawText = "דוד המלך"),
            isDragHovering = false,
        )
    }
}

@Preview(showBackground = true, name = "KeyItem — hovering")
@Composable
private fun KeyItemHoveringPreview() {
    PreviewTheme {
        PairMatchingKeyItemView(
            item = KeyItem(id = 0, rawText = "דוד המלך"),
            isDragHovering = true,
        )
    }
}

@Preview(showBackground = true, name = "KeyItem — matched")
@Composable
private fun KeyItemMatchedPreview() {
    PreviewTheme {
        PairMatchingKeyItemView(
            item = KeyItem(id = 0, rawText = "דוד המלך", matchedValueRaw = "חברון"),
            isDragHovering = false,
        )
    }
}

@Preview(showBackground = true, name = "KeyItem — matched with bold asterisks")
@Composable
private fun KeyItemMatchedBoldPreview() {
    PreviewTheme {
        PairMatchingKeyItemView(
            item = KeyItem(id = 0, rawText = "*דוד* המלך", matchedValueRaw = "*חברון*"),
            isDragHovering = false,
        )
    }
}
